import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Mail, Phone, Calendar, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/components/auth-provider";
import { Property } from "@/types/types";
import { submitPropertyInquiry } from "@/lib/inquiry-service";
import { doc, getDoc } from "firebase/firestore";
import { getFirebaseDB } from "@/lib/firebase";

const enquirySchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(10, "Phone number must be at least 10 digits"),
  moveInDate: z.string().min(1, "Please select a move-in date"),
  message: z.string().min(10, "Message must be at least 10 characters"),
  contactPreference: z.enum(["email", "phone", "either"]),
});

type EnquiryFormData = z.infer<typeof enquirySchema>;

interface PropertyEnquiryDialogProps {
  property: Property;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PropertyEnquiryDialog({
  property,
  open,
  onOpenChange,
}: PropertyEnquiryDialogProps) {
  const navigate = useNavigate();
  const { user, status } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<EnquiryFormData>({
    resolver: zodResolver(enquirySchema),
    defaultValues: {
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phoneNumber || "",
      moveInDate: "",
      message: `Hi, I'm interested in viewing this property: ${property.title}`,
      contactPreference: "either",
    },
  });

  const onSubmit = async (data: EnquiryFormData) => {
    // Require authentication for submitting enquiries
    if (status !== "authenticated") {
      toast.error("Please sign in to submit an enquiry");
      onOpenChange(false);
      navigate(`/auth/signin?callbackUrl=/properties/${property.id}`);
      return;
    }

    // Check if user is verified
    if (user && !user.emailVerified) {
      toast.error("Please verify your email before submitting enquiries");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get landlord info
      const db = getFirebaseDB();
      if (!db) throw new Error("Firebase not initialized");

      const landlordRef = doc(db, "users", property.listerId);
      const landlordSnap = await getDoc(landlordRef);

      if (!landlordSnap.exists()) {
        throw new Error("Landlord not found");
      }

      const landlordData = landlordSnap.data();

      // Submit inquiry using service
      await submitPropertyInquiry({
        propertyId: property.id,
        propertyTitle: property.title,
        landlordId: property.listerId,
        landlordName:
          landlordData.name || landlordData.displayName || "Landlord",
        landlordEmail: landlordData.email || "",
        inquirerName: data.name,
        inquirerEmail: data.email,
        inquirerPhone: data.phone,
        message: data.message,
        moveInDate: data.moveInDate,
      });

      toast.success("Enquiry submitted successfully!", {
        description: "The landlord will contact you soon.",
      });

      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      toast.error("Failed to submit enquiry", {
        description:
          error instanceof Error ? error.message : "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Enquire About This Property</DialogTitle>
          <DialogDescription>
            Fill out the form below to express your interest in{" "}
            <span className="font-semibold">{property.title}</span>. The
            landlord will review your enquiry and contact you.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          className="pl-10"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="tel"
                          placeholder="+234 xxx xxxx xxx"
                          className="pl-10"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="moveInDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Move-in Date</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="date"
                        className="pl-10"
                        min={new Date().toISOString().split("T")[0]}
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactPreference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Contact Method</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select contact method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="either">Either</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message to Landlord</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Tell the landlord why you're interested..."
                      className="resize-none"
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Include any questions or special requirements
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Submit Enquiry
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
